import { Controller } from "@hotwired/stimulus"
import { CutoutPlayer } from "./jane_cutout_player"
import { portraitAppearance } from "../utils/avatar_appearance"
import { burstConfetti } from "../utils/daily_celebration"
import { splitWords, wordTimes, wordAt, SpokenBubble } from "../utils/spoken_words"

// The owner's digital twin standing on their public page (/u/:slug,
// ProfilePage twin sidepanel). It plays only clips that already
// exist: a wave hello, an idle loop with the odd glance, listening while you
// point at a link, a nod when you open one, and asleep while it's night where
// the owner lives. Tap an "Ask me" question and it thinks while the recording
// loads, then talks - the answer the owner saved, recorded once, with a
// caption - and nods. Nothing here calls a model: the page is free to visit.
// Without WebGL, or with reduced motion, the face stays a still image and the
// answers still play.
const IDLE = ["rest", "rest", "rest_2", "rest_3", "idle"]
const FIDGETS = ["idle_glance", "idle_adjust", "idle_smile"]
const TALK = ["talking", "talking_2", "talking_3"]
const PROFILE_PANEL_RATIO_KEY = "emailpa-profile-panel-ratio"

export default class extends Controller {
  static targets = ["canvas", "face", "caption", "ask", "note", "notePlay", "pill", "menu", "messageStatus", "messageSend", "resizeHandle"]
  static values = { appearance: String, clips: Object, aspect: Number, timeZone: String, name: String, answers: Array, note: Object }

  connect() {
    this._twinFloating = this.element.classList.contains("is-twin-floating")
    this._syncAvatarToggle()
    this._syncAppearance()
    this._onFaceReady = () => this.element.querySelector(".profile-stage")?.classList.add("is-face-ready")
    if (this.hasFaceTarget) {
      this.faceTarget.addEventListener("load", this._onFaceReady)
      this.faceTarget.addEventListener("error", this._onFaceReady)
      if (this.faceTarget.complete) this._onFaceReady()
    }
    this._clock = setInterval(() => this._checkNight(), 60_000)
    this._onPanelPointerMove = (event) => this._resizePanel(event)
    this._onPanelPointerUp = (event) => this._endPanelResize(event)
    this._onPanelResize = () => { this._syncAppearance(); this._syncPanelResize(); this._syncFloatingPosition() }
    this._restorePanelRatio()
    requestAnimationFrame(() => this._syncFloatingPosition())
    this._composerResize = new ResizeObserver(() => this._syncFloatingPosition())
    this.element.querySelectorAll('.profile-messages-sidebar, .profile-messages-twin-composer').forEach(element => this._composerResize.observe(element))
    window.addEventListener("resize", this._onPanelResize)
    // The navigation above the reading pane takes its width from it
    // (--profile-navigation-width). Page load settles that width after
    // connect - the rail, a scrollbar, fonts - without a window resize, so
    // follow the pane itself or the bar overlaps the avatar until a drag.
    // (ResizeObserver already runs once per frame; the navigation it resizes
    // is outside what it observes, so this cannot loop.)
    this._mainResize = new ResizeObserver(() => this._syncPanelResize())
    const main = this.element.querySelector(".profile-home-main")
    if (main) this._mainResize.observe(main)
    this._mainResize.observe(this.element)
    // Messages docking beside the page adds a grid column (_profile_page):
    // re-fit the split and tell the panel where the avatar starts.
    // Measured at once (no first frame with the panel over the feed) and again
    // once the new column has settled.
    this._onPanelsChanged = () => {
      this._syncPanelResize()
      requestAnimationFrame(() => this._syncPanelResize())
    }
    window.addEventListener("jane:panels-changed", this._onPanelsChanged)
    // A birthday or congratulations note opens with a little confetti, and
    // saves the big burst for when the twin has said it.
    if (this.noteValue?.festive && !this._reducedMotion()) {
      this._welcome = setTimeout(() => burstConfetti([{ particleCount: 70, spread: 70, origin: { y: 0.35 } }]), 700)
    }
    this._outside = (event) => {
      if (this.hasPillTarget && !this.pillTarget.contains(event.target)) this._closePill()
    }
    this._escape = (event) => {
      if (event.key !== "Escape") return
      if (this.element.querySelector(".profile-stage")?.classList.contains("is-twin-fullscreen")) {
        this._setStageFullscreen(false)
      } else {
        this._closePill()
      }
    }
    document.addEventListener("click", this._outside)
    document.addEventListener("keydown", this._escape)
    this._onAppearance = event => {
      if (!["character", "portrait"].includes(event.detail?.appearance)) return
      this.appearanceValue = event.detail.appearance
      this._syncAppearance()
      this._syncFloatingPosition()
    }
    window.addEventListener("jane:appearance", this._onAppearance)
    this._appearanceReady = true
    this._startAnimation()
  }

  _syncAppearance() {
    const portrait = this._twinFloating && !this.element.querySelector('.is-twin-fullscreen') && portraitAppearance(this.appearanceValue)
    if (portrait === this._portrait) return
    this._portrait = portrait
    this.element.classList.toggle("is-avatar-portrait", portrait)
    if (portrait) {
      clearTimeout(this._fidget)
      this.player?.pause()
    } else if (this._appearanceReady) this._startAnimation()
  }

  _startAnimation() {
    if (this._portrait) return
    if (this.player) { this.player.resume(); this._idle(); return }
    const still = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    if (still || !Object.keys(this.clipsValue).length || !CutoutPlayer.supported()) return

    try {
      this.player = new CutoutPlayer(this.canvasTarget)
    } catch (_error) {
      return
    }
    this.canvasTarget.style.aspectRatio = String(this.aspectValue || 0.6)
    // The still face stays until a clip is really playing, then the cut-out
    // takes its place.
    this.player.video.addEventListener("playing", () => {
      this.canvasTarget.hidden = false
      this.element.classList.add("is-alive")
    }, { once: true })
    // Sharp for the size it's shown at: the floating avatar plays the web
    // cut-outs (312px wide), the docked or full-screen stage the high-res ones
    // and a larger face. Re-checked whenever the stage changes size - switching
    // to the sidebar or full screen, dragging the divider - not only the window.
    this._syncQuality()
    this._qualityResize = new ResizeObserver(() => this._syncQuality())
    this._qualityResize.observe(this.canvasTarget.parentElement)
    this.player.preload(this._url(this._choose(IDLE)))
    this._asleep() ? this._loop("sleeping") : this._once("wave", () => this._idle())
    this._onVisibility = () => (document.hidden || this._portrait ? this.player?.pause() : this.player?.resume())
    document.addEventListener("visibilitychange", this._onVisibility)
  }

  disconnect() {
    window.removeEventListener("jane:appearance", this._onAppearance)
    this._appearanceReady = false
    this._composerResize?.disconnect()
    this._avatarUp?.()
    clearInterval(this._clock)
    clearTimeout(this._welcome)
    document.removeEventListener("click", this._outside)
    document.removeEventListener("keydown", this._escape)
    if (this.hasFaceTarget) {
      this.faceTarget.removeEventListener("load", this._onFaceReady)
      this.faceTarget.removeEventListener("error", this._onFaceReady)
    }
    clearTimeout(this._fidget)
    this._hush()
    document.removeEventListener("visibilitychange", this._onVisibility)
    this._qualityResize?.disconnect()
    this._demoing = false
    window.removeEventListener("resize", this._onPanelResize)
    this._mainResize?.disconnect()
    window.removeEventListener("jane:panels-changed", this._onPanelsChanged)
    document.documentElement.style.removeProperty("--profile-stage-dock-width")
    window.removeEventListener("pointermove", this._onPanelPointerMove)
    window.removeEventListener("pointerup", this._onPanelPointerUp)
    window.removeEventListener("pointercancel", this._onPanelPointerUp)
    this._savePanelRatio()
    document.body.classList.remove("profile-home-resizing")
    this.player?.destroy()
    this.player = null
    this._setStageFullscreen(false)
  }

  // The name in the pill: on a touch screen there's no hover, so a tap opens
  // the buttons (and a second tap, or a tap elsewhere, closes them).
  togglePill(event) {
    event.preventDefault()
    const open = !this.pillTarget.classList.contains("is-open")
    this.pillTarget.classList.toggle("is-open", open)
    event.currentTarget.setAttribute("aria-expanded", String(open))
    if (!open) this._closeMenu()
  }

  // Ask (the owner's saved questions) or Links (on a page that is just the
  // twin): a menu above the pill, one open at a time.
  toggleMenu(event) {
    event.preventDefault()
    const menu = this.menuTargets.find((el) => el.dataset.menu === event.params.menu)
    if (!menu) return
    const open = !menu.classList.contains("is-open")
    this._closeMenu()
    menu.classList.toggle("is-open", open)
    if (open) {
      this.pillTarget.classList.add("is-open")
      menu.querySelector(".profile-pill-menu-item, input:not([type=hidden]):not([tabindex='-1']), textarea")?.focus()
    }
  }

  openStage(event) {
    event?.preventDefault()
    this._setStageHidden(false)
    if (!this._portrait) this.player?.resume()
    if (window.innerWidth < 900 && !this._twinFloating) this.showStageFullscreen()
    else this.element.querySelector(this._twinFloating ? ".profile-avatar-controls .profile-avatar-toggle" : ".profile-stage-fullscreen")?.focus()
  }

  toggleAvatar(event) {
    event.preventDefault()
    this._setStageFullscreen(false)
    this._twinFloating = !this._twinFloating
    this._syncAvatarToggle()
    this.element.classList.toggle("is-twin-floating", this._twinFloating)
    const stage = this.element.querySelector('.profile-stage')
    for (const property of ['left', 'top', 'right', 'bottom']) stage.style[property] = ''
    this._setStageHidden(false)
    this._syncFloatingPosition()
    if (!this._portrait) this.player?.resume()
    if (window.innerWidth < 900 && !this._twinFloating) this.showStageFullscreen()
    requestAnimationFrame(() => this.element.querySelector(this._twinFloating ? '.profile-avatar-controls button' : '.profile-stage-header button')?.focus())
  }

  _syncAvatarToggle() {
    const label = this._twinFloating ? "Show sidebar" : "Show floating avatar"
    this.element.querySelectorAll('.profile-avatar-toggle').forEach(button => {
      button.title = label
      button.setAttribute('aria-label', label)
      button.setAttribute('aria-pressed', String(this._twinFloating))
    })
  }

  _syncFloatingPosition() {
    this._syncAppearance()
    if (!this._twinFloating) return
    const stage = this.element.querySelector('.profile-stage')
    if (stage.style.top) return
    const trays = [document.querySelector('.assistant-bottom-nav'), ...this.element.querySelectorAll('.profile-messages-twin-composer, .messages-thread-composer-wrap')]
    const tops = trays.filter(Boolean).map(element => element.getBoundingClientRect()).filter(rect => rect.height > 0 && rect.top > 0).map(rect => rect.top)
    const top = tops.length ? Math.min(...tops) : innerHeight
    stage.style.bottom = `${Math.max(80, top > 0 ? innerHeight - top + 12 : 80)}px`
  }

  startAvatarDrag(event) {
    if (!this._twinFloating || event.button !== 0) return
    const stage = this.element.querySelector('.profile-stage')
    const rect = stage.getBoundingClientRect()
    const offset = { x: event.clientX - rect.left, y: event.clientY - rect.top }
    this._avatarMove = e => {
      stage.style.right = 'auto'
      stage.style.bottom = 'auto'
      stage.style.left = `${Math.max(0, Math.min(innerWidth - rect.width, e.clientX - offset.x))}px`
      stage.style.top = `${Math.max(0, Math.min(innerHeight - rect.height, e.clientY - offset.y))}px`
    }
    this._avatarUp = () => {
      window.removeEventListener('pointermove', this._avatarMove)
      window.removeEventListener('pointerup', this._avatarUp)
      window.removeEventListener('pointercancel', this._avatarUp)
    }
    window.addEventListener('pointermove', this._avatarMove)
    window.addEventListener('pointerup', this._avatarUp)
    window.addEventListener('pointercancel', this._avatarUp)
    event.preventDefault()
  }

  closeStage(event) {
    event.preventDefault()
    this._setStageFullscreen(false)
    this._setStageHidden(true)
    this._hush()
    this._caption("")
    if (this.player) { this._rest(); this.player.pause() }
    this.element.closest(".public-profile")?.querySelector(".profile-card-utilities-toggle")?.focus()
  }

  _setStageHidden(hidden) {
    this._twinHidden = hidden
    this.element.classList.toggle("is-twin-hidden", hidden)
    this.element.closest(".public-profile")?.querySelector(".profile-home-twin-toggle")?.setAttribute("aria-expanded", String(!hidden))
    if (!hidden) this._syncPanelResize()
  }

  showStageFullscreen() {
    this._fullscreenReturnFocus = document.activeElement
    this._setStageFullscreen(true)
    this.element.querySelector(".profile-stage-fullscreen")?.focus()
  }

  toggleStageFullscreen(event) {
    const stage = this.element.querySelector(".profile-stage")
    this._setStageFullscreen(!stage?.classList.contains("is-twin-fullscreen"), event.currentTarget)
  }

  _setStageFullscreen(fullscreen, button = this.element.querySelector(".profile-stage-fullscreen")) {
    this.element.querySelector(".profile-stage")?.classList.toggle("is-twin-fullscreen", fullscreen)
    document.documentElement.classList.toggle("profile-twin-fullscreen", fullscreen)
    this._syncAppearance()
    const header = this.element.closest(".public-profile")?.querySelector(".profile-home-navigation")
    if (header) header.inert = fullscreen
    this.element.querySelectorAll(".profile-home-main, .profile-home-resizer").forEach(element => { element.inert = fullscreen })
    const rail = document.querySelector(".public-profile-rail")
    if (rail) rail.inert = fullscreen
    if (!fullscreen && this._fullscreenReturnFocus) {
      const returnFocus = this._fullscreenReturnFocus.getClientRects().length ? this._fullscreenReturnFocus : this.element.closest(".public-profile")?.querySelector(".profile-breadcrumb-root")
      returnFocus?.focus()
      this._fullscreenReturnFocus = null
    }
    if (!button) return

    const icon = button.querySelector("i")
    const label = fullscreen ? "Exit full screen" : `Expand ${this.nameValue}`
    button.setAttribute("aria-label", label)
    button.setAttribute("title", label)
    button.setAttribute("aria-pressed", String(fullscreen))
    if (icon) icon.className = `bi ${fullscreen ? "bi-arrows-angle-contract" : "bi-arrows-angle-expand"}`
  }

  startResize(event) {
    if (event.button !== 0 || !this.hasResizeHandleTarget) return
    event.preventDefault()
    this._resizePointerId = event.pointerId
    // Keep the drag when the pointer crosses an iframe (a video or LinkedIn
    // embed in the feed), which would otherwise swallow every pointermove.
    this._resizeCapture = event.currentTarget
    try { this._resizeCapture.setPointerCapture(event.pointerId) } catch (_error) { /* no live pointer (synthetic events) */ }
    this._resizeStartX = event.clientX
    this._resizeStartWidth = this.element.querySelector(".profile-home-main").getBoundingClientRect().width
    document.body.classList.add("profile-home-resizing")
    window.addEventListener("pointermove", this._onPanelPointerMove)
    window.addEventListener("pointerup", this._onPanelPointerUp)
    window.addEventListener("pointercancel", this._onPanelPointerUp)
    this._syncPanelResize()
  }

  _resizePanel(event) {
    if (event.pointerId !== this._resizePointerId) return
    const width = this._resizeStartWidth + event.clientX - this._resizeStartX
    this._setPanelRatio(width / this.element.getBoundingClientRect().width)
  }

  _endPanelResize(event) {
    if (event.pointerId !== this._resizePointerId) return
    this._resizePointerId = null
    try { this._resizeCapture?.releasePointerCapture(event.pointerId) } catch (_error) { /* never captured */ }
    this._resizeCapture = null
    document.body.classList.remove("profile-home-resizing")
    window.removeEventListener("pointermove", this._onPanelPointerMove)
    window.removeEventListener("pointerup", this._onPanelPointerUp)
    window.removeEventListener("pointercancel", this._onPanelPointerUp)
    this._savePanelRatio()
  }

  resizeByKey(event) {
    if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return
    event.preventDefault()
    const currentWidth = this.element.querySelector(".profile-home-main").getBoundingClientRect().width
    const nextWidth = event.key === "Home" ? 0
      : event.key === "End" ? this.element.getBoundingClientRect().width
        : currentWidth + (event.key === "ArrowRight" ? 24 : -24)
    this._setPanelRatio(nextWidth / this.element.getBoundingClientRect().width)
    this._savePanelRatio()
  }

  _restorePanelRatio() {
    let savedRatio
    try {
      savedRatio = Number(window.localStorage.getItem(PROFILE_PANEL_RATIO_KEY))
    } catch (_error) {
      savedRatio = NaN
    }
    if (Number.isFinite(savedRatio) && savedRatio > 0) {
      this._setPanelRatio(savedRatio)
    } else {
      this._syncPanelResize()
    }
  }

  _setPanelRatio(ratio) {
    if (!this.hasResizeHandleTarget) return
    if (window.innerWidth < 900) { this._panelRatio = ratio; return }
    const limits = this._panelRatioLimits()
    const clamped = Math.min(limits.max, Math.max(limits.min, ratio))
    this.element.style.setProperty("--profile-home-main-width", `${clamped * 100}%`)
    this._panelRatio = clamped
    this._syncPanelResize()
  }

  _panelRatioLimits() {
    const width = this.element.getBoundingClientRect().width
    const gutterWidth = this.resizeHandleTarget.getBoundingClientRect().width
    const available = Math.max(592, width - gutterWidth)
    const min = Math.min(0.65, Math.max(0.2, 260 / available))
    return {
      min,
      max: Math.max(min, Math.min(0.65, 1 - (320 + this._messagesDockWidth()) / available))
    }
  }

  // The Messages sidebar's (or the profile chat panel's) column, when it is
  // docked between the reading pane and the avatar; 0 otherwise.
  _messagesDockWidth() {
    const root = document.documentElement
    if (!root.classList.contains("messages-docked") && !root.classList.contains("profile-chat-docked")) return 0
    // Below this it lies over the avatar instead (_profile_page).
    if (!window.matchMedia("(min-width: 1280px)").matches) return 0
    const panel = document.querySelector(".messages-drawer--sidebar:not(.hidden)")
    return panel ? panel.getBoundingClientRect().width : 0
  }

  _syncPanelResize() {
    this._syncFloatingPosition()
    if (!this.hasResizeHandleTarget || this._twinHidden || this._twinFloating || window.innerWidth < 900) return
    const rootWidth = this.element.getBoundingClientRect().width
    const main = this.element.querySelector(".profile-home-main")
    if (!rootWidth || !main) return
    const ratio = this._panelRatio || main.getBoundingClientRect().width / rootWidth
    const limits = this._panelRatioLimits()
    const current = Math.min(limits.max, Math.max(limits.min, ratio))
    const percent = Math.round(current * 100)
    this.resizeHandleTarget.setAttribute("aria-valuemin", String(Math.round(limits.min * 100)))
    this.resizeHandleTarget.setAttribute("aria-valuemax", String(Math.round(limits.max * 100)))
    this.resizeHandleTarget.setAttribute("aria-valuenow", String(percent))
    this.resizeHandleTarget.setAttribute("aria-valuetext", `${percent}% profile, ${100 - percent}% twin`)
    if (this._panelRatio) {
      this._panelRatio = current
      this.element.style.setProperty("--profile-home-main-width", `${current * 100}%`)
    }
    this.element.closest(".public-profile")?.style.setProperty("--profile-navigation-width", `${main.getBoundingClientRect().width}px`)
    // A docked Messages panel ends where the avatar's resizer begins.
    const resizer = this.resizeHandleTarget.getBoundingClientRect()
    document.documentElement.style.setProperty("--profile-stage-dock-width", `${Math.max(0, document.documentElement.clientWidth - resizer.left)}px`)
  }

  _savePanelRatio() {
    if (!Number.isFinite(this._panelRatio)) return
    try {
      window.localStorage.setItem(PROFILE_PANEL_RATIO_KEY, String(this._panelRatio))
    } catch (_error) {
      // Resizing still works for this visit if storage is unavailable.
    }
  }

  _closeMenu() {
    this.menuTargets.forEach((menu) => menu.classList.remove("is-open"))
  }

  // Leave a message: sent as it is - nothing reads it but the owner - and
  // the twin celebrates.
  async send(event) {
    event.preventDefault()
    const form = event.currentTarget
    this.messageSendTarget.disabled = true
    this.messageStatusTarget.textContent = "Sending…"
    try {
      const response = await fetch(form.action, {
        method: "POST",
        headers: { Accept: "application/json", "X-CSRF-Token": document.querySelector("meta[name=csrf-token]")?.content },
        body: new FormData(form)
      })
      const data = await response.json().catch(() => ({}))
      if (response.ok && data.ok) {
        form.reset()
        this.messageStatusTarget.textContent = `Sent. ${this.nameValue} will see it.`
        if (this.player && !this._speaking) this._once("celebrate", () => this._rest())
        setTimeout(() => this._closePill(), 2200)
      } else {
        this.messageStatusTarget.textContent = data.error || (response.status === 429 ? "That's a lot of messages - try again in a few minutes." : "That didn't send. Try again.")
      }
    } catch (_error) {
      this.messageStatusTarget.textContent = "That didn't send. Try again."
    } finally {
      this.messageSendTarget.disabled = false
    }
  }

  _closePill() {
    this._closeMenu()
    if (!this.hasPillTarget) return
    this.pillTarget.classList.remove("is-open")
    this.pillTarget.querySelector(".profile-pill-name")?.setAttribute("aria-expanded", "false")
  }

  // An "Ask me" question: think while the recording loads, talk while it
  // plays, captioned, then nod. A personal note (id "note") plays the same
  // way and ends in a celebration when there's a clip for one.
  ask(event) {
    event.preventDefault()
    this._closeMenu()
    const item = event.params.id === "note" ? this.noteValue : this.answersValue.find((answer) => answer.id === event.params.id)
    if (!item?.answer) return
    this._playingNote = item.id === "note"
    if (this._playingNote && this.hasNoteTarget) this.noteTarget.classList.add("is-playing")
    this._hush()
    this._speaking = true
    if (this._playingNote) this.notePlayTargets.forEach(button => { button.hidden = true })
    this.askTargets.forEach((chip) => chip.classList.toggle("is-current", chip === event.currentTarget))
    clearTimeout(this._fidget)
    if (this.player) this._loop("thinking")
    this._caption("")

    const talk = (seconds, clock = null) => {
      if (this.player) this._loop(this._choose(TALK) || "idle")
      this._type(item.answer, seconds, clock)
    }
    if (!item.audio) {
      const seconds = Math.max(2, item.answer.length / 15)
      this._silent = setTimeout(() => {
        talk(seconds)
        this._silent = setTimeout(() => this._done(), seconds * 1000)
      }, 600)
      return
    }
    // play() inside the tap, so the browser allows the sound.
    const audio = (this._audio ||= new Audio())
    // Can't play it (blocked, gone): show the words instead.
    const fallback = () => {
      if (!this._speaking) return
      const seconds = Math.max(2, item.answer.length / 15)
      talk(seconds)
      this._silent = setTimeout(() => this._done(), seconds * 1000)
    }
    const speak = () => {
      audio.onplaying = () => talk(audio.duration, () => audio.currentTime)
      audio.onended = () => this._done()
      audio.onerror = fallback
      audio.src = item.audio
      audio.play().catch(fallback)
    }
    if (item.clip && this.player && !this._portrait) return this._playSynced(item, audio, speak)
    speak()
  }

  // A note with its own lip-synced video (VideoNotes::Lipsync): the clip
  // plays once, and the recording starts the moment it's moving so the lips
  // land on the words. The sound is unlocked inside the tap (play, then hold)
  // because the clip only starts after it loads. If it hasn't started in a few
  // seconds, the note plays the usual way.
  _playSynced(item, audio, speak) {
    audio.onplaying = audio.onended = audio.onerror = null
    audio.src = item.audio
    this._clipRolling = false
    audio.play().then(() => { if (!this._clipRolling) audio.pause() }).catch(() => {})
    const start = () => {
      this._clipRolling = true
      clearTimeout(this._clipWait)
      audio.currentTime = 0
      audio.play().catch(() => {})
      this._type(item.answer, audio.duration || Math.max(2, item.answer.length / 15), () => audio.currentTime, item.word_times)
    }
    this.current = "note"
    this.player.play(this._hq ? `${item.clip}?hq=1` : item.clip, { once: true, onEnd: () => this._done() })
    const video = this.player.video
    video.addEventListener("playing", start, { once: true })
    this._clipWait = setTimeout(() => {
      video.removeEventListener("playing", start)
      if (this._clipRolling || !this._speaking) return
      audio.pause()
      speak()
    }, 8000)
  }

  _reducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches
  }

  // Pointing at a link: the twin listens.
  attend() {
    if (!this.player || this._asleep() || this._speaking) return
    clearTimeout(this._fidget)
    this._loop("listening") || this._idle()
  }

  release() {
    if (!this.player || this._asleep() || this._speaking) return
    this._idle()
  }

  // Opening a link: a nod on the way out.
  opened() {
    this._closeMenu()
    if (!this.player || this._asleep()) return
    clearTimeout(this._fidget)
    this._once("nod", () => this._idle())
  }

  _done() {
    this._speaking = false
    this.askTargets.forEach((chip) => chip.classList.remove("is-current"))
    const cheer = this._playingNote && this.clipsValue.celebrate ? "celebrate" : "nod"
    if (this._playingNote && this.noteValue?.ending_emoji && !this._reducedMotion()) {
      this._showEndingEmoji(this.noteValue.ending_emoji)
    } else if (this._playingNote && this.noteValue?.festive && !this._reducedMotion()) {
      burstConfetti([{ particleCount: 160, spread: 100, origin: { y: 0.55 } }, { angle: 60, origin: { x: 0, y: 0.65 } }, { angle: 120, origin: { x: 1, y: 0.65 } }])
    }
    if (this._playingNote && this.hasNoteTarget) this.noteTarget.classList.remove("is-playing")
    this._playingNote = false
    this.notePlayTargets.forEach(button => { button.hidden = false })
    if (this.player) this._once(cheer, () => this._rest())
  }

  _showEndingEmoji(emoji) {
    this._clearEndingEmoji()
    const effect = document.createElement("div")
    effect.className = "profile-note-ending-emoji"
    effect.setAttribute("aria-hidden", "true")
    for (let i = 0; i < 18; i++) {
      const piece = document.createElement("span")
      piece.textContent = emoji
      piece.style.setProperty("--emoji-x", `${8 + (i * 29 % 85)}%`)
      piece.style.setProperty("--emoji-size", `${2 + (i % 4) * 0.5}rem`)
      piece.style.setProperty("--emoji-delay", `${(i % 6) * 0.2}s`)
      piece.style.setProperty("--emoji-drift", `${(i % 2 ? 1 : -1) * (12 + i % 4 * 8)}px`)
      effect.appendChild(piece)
    }
    this.element.appendChild(effect)
    this._endingEmoji = effect
    this._endingEmojiTimeout = setTimeout(() => this._clearEndingEmoji(), 7400)
  }

  _clearEndingEmoji() {
    clearTimeout(this._endingEmojiTimeout)
    this._endingEmoji?.remove()
    this._endingEmoji = null
  }

  // Stop whatever it was saying (or showing off).
  _hush() {
    this._clearEndingEmoji()
    if (this._demoing) {
      this._demoing = false
      this._caption("")
    }
    clearTimeout(this._silent)
    clearTimeout(this._clipWait)
    this._stopTyping()
    if (this._audio) {
      this._audio.onended = this._audio.onplaying = this._audio.onerror = null
      this._audio.pause()
    }
    this._speaking = false
  }

  _rest() {
    this._asleep() ? this._loop("sleeping") : this._idle()
  }

  _caption(text) {
    if (!this.hasCaptionTarget) return
    this.captionTarget.textContent = text
    this.captionTarget.hidden = !text
  }

  // Reveal the answer word by word across the time it's spoken, and light
  // the word being said in its chat bubble (utils/spoken_words). `clock` is
  // the audio's own position, so words never drift from the sound; without
  // audio, the time since it started. `times` are real word starts, when the
  // answer has them.
  _type(text, seconds, clock = null, times = null) {
    this._stopTyping()
    const words = splitWords(text)
    if (!words.length) return
    const length = Number.isFinite(seconds) && seconds > 0 ? seconds : words.length / 2.5
    const starts = Array.isArray(times) && times.length === words.length ? times : wordTimes(words, length)
    const began = performance.now()
    const now = clock || (() => (performance.now() - began) / 1000)
    const bubble = (this._bubble = new SpokenBubble(text))
    let shown = -1
    const tick = () => {
      const index = wordAt(starts, now())
      if (index !== shown) {
        shown = index
        this._caption(words.slice(0, index + 1).join(" "))
        bubble.show(index)
      }
      if (now() < length + 0.3) this._typing = requestAnimationFrame(tick)
      else this._stopTyping(true)
    }
    tick()
  }

  // `finished`: the whole answer stays in the caption; the bubble goes plain.
  _stopTyping(finished = false) {
    cancelAnimationFrame(this._typing)
    this._typing = null
    this._bubble?.clear()
    if (!finished) this._bubble = null
  }

  _idle() {
    if (this._portrait || !this.player) return
    const kind = this._choose(IDLE)
    if (!kind) return
    this._loop(kind)
    clearTimeout(this._fidget)
    const fidget = this._choose(FIDGETS)
    if (fidget) this.player.preload(this._url(fidget))
    this._fidget = setTimeout(() => {
      fidget ? this._once(fidget, () => this._idle(), true) : this._idle()
    }, 9000 + Math.random() * 9000)
  }

  _loop(kind) {
    if (this._portrait || !this.player) return false
    const url = this._url(kind)
    if (!url) return false
    this.current = kind
    this._looping = true
    this.player.play(url)
    return true
  }

  // Web or high-res, by the stage's size in device pixels. Going big swaps in
  // the larger face and restarts a looping clip in high-res at once (a loop
  // can run 18s before the next clip would pick it up).
  // The web cut-out is 312px wide: past ~400 device pixels it would be
  // stretched (the docked stage on a retina screen or a big monitor), while the
  // floating avatar (180px, 360 on retina) stays on the light clips.
  _syncQuality() {
    const hq = window.devicePixelRatio * this.canvasTarget.parentElement.clientWidth > 400
    if (hq === this._hq) return
    this._hq = hq
    if (!hq) return
    if (this.hasFaceTarget && this.faceTarget.getAttribute("src")) {
      const face = new URL(this.faceTarget.getAttribute("src"), window.location.href)
      if (!face.searchParams.has("hq")) {
        face.searchParams.set("hq", "1")
        this.faceTarget.src = face.pathname + face.search
      }
    }
    if (this.player && this._looping && this.current && !this._speaking && !this._demoing) this._loop(this.current)
  }

  // "Play all animations" (the owner's page menu): every clip the twin has,
  // once each, with its name as the caption, then back to idle. Asking a
  // question or playing a note stops it.
  demo(event) {
    event?.preventDefault()
    if (this._portrait) this.showStageFullscreen()
    if (!this.player) return
    const kinds = Object.keys(this.clipsValue)
    if (!kinds.length) return
    if (this.element.classList.contains("is-twin-hidden")) this.openStage()
    this._hush()
    clearTimeout(this._fidget)
    const run = (this._demoRun = (this._demoRun || 0) + 1)
    this._demoing = true
    const next = (index) => {
      if (run !== this._demoRun || !this._demoing) return
      if (index >= kinds.length) {
        this._demoing = false
        this._caption("")
        return this._rest()
      }
      const kind = kinds[index]
      this._caption(`${index + 1}/${kinds.length} · ${kind.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase())}`)
      this.current = kind
      this._looping = false
      this.player.play(this._url(kind), { once: true, onEnd: () => next(index + 1) })
    }
    next(0)
  }

  _once(kind, then, atBoundary = false) {
    if (this._portrait || !this.player) return then()
    const url = this._url(kind)
    if (!url) return then()
    this.current = kind
    this._looping = false
    this.player.play(url, { once: true, onEnd: then, atBoundary })
  }

  _choose(pool) {
    const available = pool.filter((kind) => this.clipsValue[kind])
    const fresh = available.filter((kind) => kind !== this.current)
    const choices = fresh.length ? fresh : available
    return choices[Math.floor(Math.random() * choices.length)]
  }

  _url(kind) {
    const path = this.clipsValue[kind]
    if (!path) return null
    return this._hq ? `${path}?hq=1` : path
  }

  // The owner's local time, and whether it's night there.
  _now() {
    try {
      const parts = new Intl.DateTimeFormat("en-US", { timeZone: this.timeZoneValue, hour: "numeric", hourCycle: "h23" }).formatToParts(new Date())
      return Number(parts.find((part) => part.type === "hour")?.value)
    } catch (_error) {
      return new Date().getHours()
    }
  }

  _asleep() {
    const hour = this._now()
    return hour >= 23 || hour < 7
  }

  // Asleep while it's night where the owner lives.
  _checkNight() {
    if (this.player && this._asleep() && !this._speaking && this.current !== "sleeping") this._loop("sleeping")
  }
}
